/**
 * SEO Structured Data Helpers
 *
 * These helpers generate schema.org structured data for SEO purposes.
 * They use translation data to avoid duplication and ensure consistency.
 */

import { DEFAULT_ARTICLE_AUTHOR, type ContentAuthor } from '@/data/content-authors';
import { stripLocaleFromPath } from './locale-url-helper';
import i18n from '@/lib/i18n';

export const STRUCTURED_DATA_BASE_URL = 'https://heykaution.de';
export const ORGANIZATION_ID = `${STRUCTURED_DATA_BASE_URL}/#organization`;
export const WEBSITE_ID = `${STRUCTURED_DATA_BASE_URL}/#website`;
export const ORGANIZATION_LOGO_URL = `${STRUCTURED_DATA_BASE_URL}/assets/logos/hk-logo.png`;
export const DEFAULT_OG_IMAGE = `${STRUCTURED_DATA_BASE_URL}/assets/og/og-default.png`;

const ORGANIZATION_SAME_AS = [
  'https://www.facebook.com/people/heykaution/61579747530727/',
  'https://www.instagram.com/heykaution/',
  'https://linkedin.com/showcase/heykaution',
  'https://www.google.com/maps/search/?api=1&query=plusForta+GmbH,Talstra%C3%9Fe+24,40217+D%C3%BCsseldorf',
] as const;

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface PageStructuredDataOptions {
  locale?: string;
  breadcrumbs?: BreadcrumbItem[];
  blocks: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Builds the full JSON-LD graph for a public page: Organization, WebSite, page-specific blocks, optional breadcrumbs.
 */
export const buildPageStructuredData = ({
  locale,
  breadcrumbs,
  blocks,
}: PageStructuredDataOptions): Record<string, unknown>[] => {
  const pageBlocks = Array.isArray(blocks) ? blocks : [blocks];
  const data: Record<string, unknown>[] = [
    generateOrganizationStructuredData(),
    generateWebSiteStructuredData(locale),
    ...pageBlocks,
  ];

  if (breadcrumbs && breadcrumbs.length > 0) {
    data.push(generateBreadcrumbStructuredData(breadcrumbs));
  }

  return data;
};

/**
 * Site-wide Organization schema with contact details and social profiles.
 */
export const generateOrganizationStructuredData = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: 'heykaution',
  legalName: 'plusForta GmbH',
  url: `${STRUCTURED_DATA_BASE_URL}/`,
  logo: ORGANIZATION_LOGO_URL,
  sameAs: [...ORGANIZATION_SAME_AS],
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@heykaution.de',
    contactType: 'customer service',
    availableLanguage: ['German', 'English'],
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Talstraße 24',
    addressLocality: 'Düsseldorf',
    postalCode: '40217',
    addressCountry: 'DE',
  },
});

/**
 * Site-wide WebSite schema referencing the Organization publisher.
 */
export const generateWebSiteStructuredData = (locale?: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: `${STRUCTURED_DATA_BASE_URL}/`,
  name: 'heykaution',
  publisher: { '@id': ORGANIZATION_ID },
  inLanguage: (locale || 'de') === 'en' ? ['en-US', 'de-DE'] : ['de-DE', 'en-US'],
});

export interface FinancialProductStructuredDataParams {
  title: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}

/**
 * FinancialProduct schema for commercial deposit-account landing and product pages.
 */
export const generateFinancialProductStructuredData = (params: FinancialProductStructuredDataParams) => ({
  '@context': 'https://schema.org',
  '@type': 'FinancialProduct',
  name: params.title,
  description: params.description,
  url: params.url,
  image: DEFAULT_OG_IMAGE,
  provider: { '@id': ORGANIZATION_ID },
  brand: {
    '@type': 'Brand',
    name: 'heykaution',
  },
  ...(params.datePublished && { datePublished: params.datePublished }),
  ...(params.dateModified && { dateModified: params.dateModified }),
});

export interface WebPageStructuredDataParams {
  title: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
}

/**
 * Generic WebPage schema for legal and informational subpages.
 */
export const generateWebPageStructuredData = (params: WebPageStructuredDataParams) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: params.title,
  description: params.description,
  url: params.url,
  isPartOf: { '@id': WEBSITE_ID },
  publisher: { '@id': ORGANIZATION_ID },
  ...(params.datePublished && { datePublished: params.datePublished }),
  ...(params.dateModified && { dateModified: params.dateModified }),
});

/**
 * BreadcrumbList schema for subpages.
 */
export const generateBreadcrumbStructuredData = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

/**
 * Localized breadcrumb label for the site home page (translation namespace).
 */
export const getBreadcrumbHomeLabel = (): string => i18n.t('seo.breadcrumbs.home');

/**
 * Standard two-level breadcrumb: home → current page.
 */
export const buildSubpageBreadcrumbs = (
  pageTitle: string,
  locale: string | undefined,
  currentPath: string,
): BreadcrumbItem[] => [
  { name: getBreadcrumbHomeLabel(), url: getCanonicalUrl('', locale) },
  { name: pageTitle, url: getCanonicalUrl(currentPath, locale) },
];

export interface FaqItem {
  question: string;
  answer: string | {
    intro?: string;
    list?: string[];
    important?: string;
    explanation?: string;
    requirements?: Array<{ label: string; description: string }>;
    residential?: { title: string; points: string[] };
    commercial?: { title: string; points: string[] };
  };
}

/**
 * Strips <link>...</link> markup from FAQ answer text for JSON-LD output.
 */
export const stripLinkMarkup = (text: string): string =>
  text.replace(/<link>/g, '').replace(/<\/link>/g, '');

/**
 * Generates FAQ structured data from translation objects
 *
 * @param faqs - Array of FAQ items from translations
 * @returns Schema.org FAQPage structured data
 */
export const generateFaqStructuredData = (faqs: FaqItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => {
    let answerText: string;

    if (typeof faq.answer === 'string') {
      answerText = faq.answer;
    } else {
      // Complex answer object - flatten to text
      const parts: string[] = [];

      if (faq.answer.intro) {
        parts.push(faq.answer.intro);
      }

      if (faq.answer.list) {
        parts.push(faq.answer.list.join(', '));
      }

      if (faq.answer.important) {
        parts.push(faq.answer.important);
      }

      if (faq.answer.explanation) {
        parts.push(faq.answer.explanation);
      }

      if (faq.answer.requirements) {
        const reqText = faq.answer.requirements
          .map(req => `${req.label}: ${req.description}`)
          .join('; ');
        parts.push(reqText);
      }

      if (faq.answer.residential) {
        parts.push(`${faq.answer.residential.title}: ${faq.answer.residential.points.join('; ')}`);
      }

      if (faq.answer.commercial) {
        parts.push(`${faq.answer.commercial.title}: ${faq.answer.commercial.points.join('; ')}`);
      }

      answerText = parts.join(' ');
    }

    return {
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": stripLinkMarkup(answerText)
      }
    };
  })
});

export interface ArticleStructuredDataParams {
  title: string;
  description: string;
  url: string;
  datePublished?: string;
  dateModified?: string;
  image?: string;
  author?: ContentAuthor;
  locale?: string;
}

const resolveAuthorJobTitle = (author: ContentAuthor, locale?: string): string => {
  return author.jobTitle[(locale || 'de') === 'en' ? 'en' : 'de'];
};

const resolveAuthorImageUrl = (author: ContentAuthor): string => {
  const path = author.image.replace(/^\//, '');

  return `${STRUCTURED_DATA_BASE_URL}/assets/${path}`;
};

const buildArticleAuthorStructuredData = (author: ContentAuthor, locale?: string) => {
  const jobTitle = resolveAuthorJobTitle(author, locale);

  return {
    '@type': 'Person' as const,
    name: author.name,
    jobTitle,
    image: resolveAuthorImageUrl(author),
    ...(author.url && { url: author.url }),
  };
};

export interface VideoObjectStructuredDataParams {
  title: string;
  description: string;
  thumbnailUrl: string;
  contentUrl: string;
  uploadDate?: string;
}

/**
 * VideoObject schema for dedicated video playback pages.
 * `embedUrl` is omitted: these pages play the file inline and have no embeddable player.
 */
export const generateVideoObjectStructuredData = (params: VideoObjectStructuredDataParams) => ({
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: params.title,
  description: params.description,
  thumbnailUrl: params.thumbnailUrl,
  contentUrl: params.contentUrl,
  publisher: { '@id': ORGANIZATION_ID },
  ...(params.uploadDate && { uploadDate: params.uploadDate }),
});

/**
 * Generates Article structured data for editorial content pages.
 */
export const generateArticleStructuredData = (params: ArticleStructuredDataParams) => {
  const author = params.author ?? DEFAULT_ARTICLE_AUTHOR;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: params.title,
    description: params.description,
    image: params.image || DEFAULT_OG_IMAGE,
    author: buildArticleAuthorStructuredData(author, params.locale),
    publisher: {
      '@id': ORGANIZATION_ID,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': params.url,
    },
    url: params.url,
    ...(params.datePublished && { datePublished: params.datePublished }),
    ...(params.dateModified && { dateModified: params.dateModified }),
  };
};

const BRAND_SUFFIX = ' | heykaution';

/**
 * Formats the document title for Inertia SSR and client navigation.
 * Appends the brand suffix once; titles that already mention heykaution pass through unchanged.
 */
export const formatDocumentTitle = (title: string): string => {
  const trimmed = title.trim();

  if (!trimmed) {
    return 'heykaution';
  }

  if (/heykaution/i.test(trimmed)) {
    return trimmed;
  }

  return `${trimmed}${BRAND_SUFFIX}`;
};

const OG_SITE_NAME = 'heykaution';

export interface SocialMetaTag {
  /** Stable key passed to Inertia `head-key` on each rendered `<meta>` tag */
  key: string;
  /** Attributes to spread onto the `<meta>` element (e.g. `property`/`name` + `content`) */
  attrs: Record<string, string>;
}

export interface SocialMetaParams {
  title: string;
  description: string;
  /** Canonical URL of the page (used for og:url) */
  url: string;
  /** Current locale (e.g. 'de', 'en'); maps to og:locale */
  locale?: string;
  /** Optional override for the share image; defaults to DEFAULT_OG_IMAGE */
  image?: string;
  /** Open Graph type; defaults to 'website' */
  type?: string;
}

/**
 * Maps an app locale to an Open Graph locale string.
 *
 * @param locale - The current locale (e.g. 'de', 'en')
 * @returns Open Graph locale (e.g. 'de_DE', 'en_US')
 */
const getOgLocale = (locale?: string): string => ((locale || 'de') === 'en' ? 'en_US' : 'de_DE');

const getOgLocaleAlternate = (locale?: string): string => ((locale || 'de') === 'en' ? 'de_DE' : 'en_US');

/**
 * Generates Open Graph + Twitter Card meta tags for social sharing and richer
 * AI/social fallbacks. Reuses the page's existing title/description values.
 *
 * @param params - Social meta parameters
 * @returns Array of meta tag descriptors to render inside the page `<Head>`
 */
export const generateSocialMetaTags = (params: SocialMetaParams): SocialMetaTag[] => {
  const image = params.image || DEFAULT_OG_IMAGE;
  const type = params.type || 'website';

  return [
    { key: 'og:type', attrs: { property: 'og:type', content: type } },
    { key: 'og:site_name', attrs: { property: 'og:site_name', content: OG_SITE_NAME } },
    { key: 'og:title', attrs: { property: 'og:title', content: params.title } },
    { key: 'og:description', attrs: { property: 'og:description', content: params.description } },
    { key: 'og:url', attrs: { property: 'og:url', content: params.url } },
    { key: 'og:locale', attrs: { property: 'og:locale', content: getOgLocale(params.locale) } },
    { key: 'og:locale:alternate', attrs: { property: 'og:locale:alternate', content: getOgLocaleAlternate(params.locale) } },
    { key: 'og:image', attrs: { property: 'og:image', content: image } },
    { key: 'og:image:width', attrs: { property: 'og:image:width', content: '1200' } },
    { key: 'og:image:height', attrs: { property: 'og:image:height', content: '630' } },
    { key: 'twitter:card', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
    { key: 'twitter:title', attrs: { name: 'twitter:title', content: params.title } },
    { key: 'twitter:description', attrs: { name: 'twitter:description', content: params.description } },
    { key: 'twitter:image', attrs: { name: 'twitter:image', content: image } },
  ];
};

/**
 * Generates the canonical URL for the current page.
 * Always returns the production URL regardless of environment (local, staging, prod).
 *
 * @param path - The current path from request (may or may not have leading slash)
 * @param locale - The current locale (e.g., 'de', 'en')
 * @returns Full canonical URL pointing to production
 */
export const getCanonicalUrl = (path: string, locale?: string): string => {
  const canonicalBase = 'https://heykaution.de';
  const cleanPath = stripLocaleFromPath(path);
  const effectiveLocale = locale || 'de';

  // Canonical form has no trailing slash (PF-11716): the homepage is /de, not
  // /de/, matching the redirect target and the sitemap.
  return `${canonicalBase}/${effectiveLocale}${cleanPath}`.replace(/\/+$/, '');
};

/**
 * Generates hreflang tags for multilingual SEO.
 * Strips any locale prefix from the path and generates correct URLs for all locales.
 *
 * @param currentPath - The current path from request (may or may not have leading slash)
 * @returns Array of hreflang tag objects
 */
export const generateHreflangTags = (currentPath: string): Array<{ hreflang: string; href: string }> => {
  const canonicalBase = 'https://heykaution.de';
  const cleanPath = stripLocaleFromPath(currentPath);

  // No trailing slash on the homepage (PF-11716), matching the canonical form.
  const deHref = `${canonicalBase}/de${cleanPath}`.replace(/\/+$/, '');
  const enHref = `${canonicalBase}/en${cleanPath}`.replace(/\/+$/, '');

  return [
    { hreflang: 'de', href: deHref },
    { hreflang: 'en', href: enHref },
    { hreflang: 'x-default', href: deHref },
  ];
};
